import React, { useMemo, useState } from "react";
import { Modal, LoadingState } from "./ui";
import { MathText } from "./MathText";
import { richTextToPlain } from "../utils/richText";
import { QUESTION_TYPE_LABELS, matchLabel } from "../utils/questionTypes";
import {
  previewQuestionImport,
  commitQuestionImport,
  undoImportBatch,
  downloadImportTemplate,
} from "../utils/api";
import {
  downloadSampleCsv,
  downloadSectionsSampleCsv,
  downloadSubjectsSampleCsv,
} from "../utils/questionImportSample";
import { showConfirmation, showError, showSuccess } from "../utils/sweetAlert";
import "./ImportQuestions.css";

const MAX_CSV_BYTES = 5 * 1024 * 1024;
// Images ZIP upload is disabled for now
// const MAX_ZIP_BYTES = 50 * 1024 * 1024;
const MAX_ERROR_GROUPS = 20;
const ROWS_PAGE = 100;

const MODES = [
  {
    value: "append",
    label: "Add",
    help: "Adds every row as a new question after the existing ones.",
  },
  {
    value: "upsert",
    label: "Update",
    help: (
      <>
        Rows with a <code>question_id</code> update that question; rows without one are added. Use this with Export
        CSV. A blank image cell keeps the current image; <code>REMOVE</code> deletes it.
      </>
    ),
  },
  {
    value: "replace",
    label: "Replace",
    danger: true,
    help: "Deletes this section's existing questions, then adds the rows. Only sections that appear in the file are affected. Not allowed once students have attempted the test series.",
  },
];

export const MODE_LABELS = { append: "Add", upsert: "Update", replace: "Replace" };

const TEMPLATE_LINKS = [
  ["ALL", "All columns"],
  ["SINGLE_CORRECT", "Single"],
  ["MULTI_CORRECT", "Multi"],
  ["NUMERICAL", "Numerical"],
  ["RANGE", "Range"],
  ["MATCH", "Match"],
  ["PARAGRAPH", "Paragraph"],
];

const ROW_FILTERS = [
  ["all", "All"],
  ["errors", "With errors"],
  ["warnings", "With warnings"],
  ["create", "New"],
  ["update", "Updates"],
];

const SHORT_TYPE_LABELS = {
  SINGLE_CORRECT: "Single",
  MULTI_CORRECT: "Multi",
  NUMERICAL: "Numerical",
  MATCH: "Match",
  PARAGRAPH: "Paragraph",
  RANGE: "Range",
};

const plural = (n, word, pluralWord = `${word}s`) => `${n} ${n === 1 ? word : pluralWord}`;

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

// Count data rows the way the server does: quote-aware, header and blank rows excluded
const countCsvRows = (text) => {
  let rows = 0;
  let inQuotes = false;
  let hasContent = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (hasContent) rows += 1;
      hasContent = false;
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      continue;
    }
    if (ch !== "," && ch !== "﻿" && !/\s/.test(ch)) hasContent = true;
  }
  if (hasContent) rows += 1;
  return Math.max(0, rows - 1);
};

// Shorten text without cutting through a \( … \) or $$ … $$ expression
const truncateMath = (text, max = 120) => {
  if (!text || text.length <= max) return text || "";
  let cut = max;
  const head = text.slice(0, cut);
  const openInline = head.split("\\(").length - head.split("\\)").length;
  if (openInline > 0) {
    const close = text.indexOf("\\)", cut);
    if (close !== -1) cut = close + 2;
  } else if ((head.match(/\$\$/g) || []).length % 2 === 1) {
    const close = text.indexOf("$$", cut);
    if (close !== -1) cut = close + 2;
  }
  return cut >= text.length ? text : `${text.slice(0, cut).trimEnd()} …`;
};

const formatRanges = (ranges) =>
  (ranges || []).map((r) => (r.min === r.max ? `${r.min}` : `${r.min}–${r.max}`)).join(", ");

const answerOf = (row) => {
  if (row.question_type === "NUMERICAL") {
    return row.numerical_value != null && row.numerical_value !== ""
      ? String(row.numerical_value)
      : formatRanges(row.numerical_ranges);
  }
  return row.correct_answer || "";
};

// Image uploads are disabled, so drop the server's image/ZIP warnings
// (e.g. "Image 'q9a.png' not found in the ZIP") and keep the row counts in step
const isImageWarning = (w) => /image/i.test(w.column || "") || /\bzip\b/i.test(w.message || "");

const withoutImageWarnings = (data) => {
  const hidden = (data.warnings || []).filter(isImageWarning);
  if (!hidden.length) return data;
  const hiddenByRow = new Map();
  hidden.forEach((w) => hiddenByRow.set(w.row, (hiddenByRow.get(w.row) || 0) + 1));
  return {
    ...data,
    warnings: data.warnings.filter((w) => !isImageWarning(w)),
    rows: (data.rows || []).map((r) =>
      hiddenByRow.has(r.row) ? { ...r, warning_count: Math.max(0, (r.warning_count || 0) - hiddenByRow.get(r.row)) } : r
    ),
  };
};

// { row → [messages] } with file-level (row: null) messages under the key "file"
const groupByRow = (items = []) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = item.row == null ? "file" : item.row;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  const file = groups.get("file");
  groups.delete("file");
  const rows = [...groups.entries()].sort((a, b) => a[0] - b[0]);
  return file ? [["file", file], ...rows] : rows;
};

const questionsLabel = ({ to_create: create, to_update: update, to_delete: del }) => {
  if (del > 0) return `delete ${del} and import ${create}`;
  if (update > 0 && create > 0) return `import ${create}, update ${update}`;
  if (update > 0) return `update ${plural(update, "question")}`;
  if (create > 0) return `import ${plural(create, "question")}`;
  return null;
};

// "Create 2 sections and import 88 questions", "Update 1 section", "Import 38 questions"…
const confirmLabel = (summary) => {
  const sectionParts = [
    summary.subjects_to_create > 0 && `create ${plural(summary.subjects_to_create, "subject")}`,
    summary.subjects_to_update > 0 && `update ${plural(summary.subjects_to_update, "subject")}`,
    summary.sections_to_create > 0 && `create ${plural(summary.sections_to_create, "section")}`,
    summary.sections_to_update > 0 && `update ${plural(summary.sections_to_update, "section")}`,
  ].filter(Boolean);
  const questions = questionsLabel(summary);
  const text = [sectionParts.join(", "), questions].filter(Boolean).join(" and ") || "import 0 questions";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Shared by the done step and the import history list
export const confirmUndoImport = (questions, sections = 0) =>
  showConfirmation(
    [
      (questions > 0 || !sections) &&
        `This deletes the ${plural(questions, "question")} this import added. Questions it updated are not changed back, and questions it replaced are not restored.`,
      sections > 0 &&
        `${questions > 0 ? "It also deletes" : "This deletes"} the ${plural(sections, "section")} this import created, unless questions were added to them later. Section settings it changed are not changed back.`,
    ]
      .filter(Boolean)
      .join(" "),
    "Undo this import?",
    "Undo import",
    "Keep them",
    "danger"
  );

// Every preview from an API with subject support says whether the test is split into subjects
const serverLacksSubjects = (data) =>
  data &&
  !("nested_sections" in data) &&
  (data.warnings || []).some((w) => /unknown columns ignored:.*\bsubject\b/i.test(w.message || ""));

const OUTDATED_SERVER_MESSAGE =
  "Nothing was imported. The server is running an older version that does not support subjects, so the " +
  "'subject' and 'subject_description' columns would be ignored and every row would go into plain sections. " +
  "Upload the backend update to the server and run 'php artisan migrate', then create the test with " +
  "'Subjects with sections' and import again.";

const sectionKeyOf = (item) => item.section_key || (item.section_id != null ? `id:${item.section_id}` : item.section_name);

function Messages({ groups, limit, idPrefix }) {
  const shown = limit ? groups.slice(0, limit) : groups;
  return (
    <ul className="iq-messages">
      {shown.map(([row, items]) => (
        <li key={row} id={idPrefix ? `${idPrefix}${row}` : undefined}>
          <strong>{row === "file" ? "File" : `Row ${row}`}</strong>
          <ul>
            {items.map((item, i) => (
              <li key={i}>
                {item.column && <code className="iq-col">{item.column}</code>} {item.message}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function FilePicker({ id, label, hint, accept, file, onChange, icon }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      {file ? (
        <div className="iq-file-chosen">
          <i className={icon}></i>
          <div className="iq-file-name">
            <strong>{file.name}</strong>
            <span>{formatSize(file.size)}</span>
          </div>
          <label htmlFor={id} className="btn-secondary btn-sm">
            Change
          </label>
          <button
            type="button"
            className="btn-ghost btn-icon btn-sm"
            onClick={() => onChange(null)}
            aria-label={`Remove ${file.name}`}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      ) : (
        <label
          htmlFor={id}
          className={`iq-drop ${dragging ? "dragging" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files?.[0]) onChange(e.dataTransfer.files[0]);
          }}
        >
          <i className={icon}></i>
          <span>
            <strong>Choose a file</strong> or drag it here
          </span>
        </label>
      )}
      <input
        id={id}
        type="file"
        accept={accept}
        className="iq-file-input"
        onChange={(e) => {
          onChange(e.target.files?.[0] || null);
          e.target.value = ""; // allow re-selecting the same file after editing it
        }}
      />
      {hint && <span className="form-hint">{hint}</span>}
    </div>
  );
}

function CsvHelp({ isSeries, timedSections, nested }) {
  return (
    <details className="iq-help">
      <summary>
        <i className="bi bi-question-circle"></i> How to fill the CSV
        <i className="bi bi-chevron-down iq-caret"></i>
      </summary>
      <ul>
        <li>
          <strong>Save as UTF-8.</strong> In Excel choose <em>File → Save As → CSV UTF-8 (Comma delimited)</em>. In
          Google Sheets choose <em>File → Download → CSV</em>. A plain Excel "CSV" breaks Hindi text.
        </li>
        <li>
          <strong>
            Only <code>question_en</code> is always required.
          </strong>{" "}
          Leave out columns you don't need. Columns can be in any order.
        </li>
        <li>
          <strong>Question types:</strong> <code>SINGLE_CORRECT</code> (or <code>SINGLE</code>, <code>MCQ</code>),{" "}
          <code>MULTI_CORRECT</code> (<code>MULTI</code>), <code>NUMERICAL</code> (<code>NUM</code>),{" "}
          <code>MATCH</code>, <code>PARAGRAPH</code> (<code>PARA</code>), <code>RANGE</code>. A blank type means
          single correct.
        </li>
        <li>
          <strong>Options:</strong> <code>option_a_en</code> … <code>option_f_en</code>, 4 to 6 in order with no
          gaps. Hindi goes in <code>option_a_hi</code>, and so on.
        </li>
        <li>
          <strong>Answers:</strong> single <code>B</code> · multi <code>A,C</code> · match{" "}
          <code>A-Q,B-P,C-S,D-R</code> (Column II goes in <code>option_p_en</code> … <code>option_u_en</code>; students
          see Column I as P, Q, R, S… and Column II as I, II, III, IV…, so <code>A-Q</code> shows as P → II) ·
          range <code>2-5</code> · numerical goes in the <code>numerical_value</code> column. Multi-correct
          questions get partial marking automatically (no extra column). Use 4 options so each correct option
          chosen is worth +1 on a 4-mark question.
        </li>
        <li>
          <strong>Marks:</strong> fill <code>marks</code> on every question. Sections have no default marks, so a
          blank <code>marks</code> cell is an error. Negative marks can be typed as <code>1</code> or <code>-1</code>,
          and a blank means no negative marking.
        </li>
        <li>
          <strong>Maths:</strong> type LaTeX as you would in the form, e.g. <code>\( \frac{"{1}{2}"} \)</code> or{" "}
          <code>\ce{"{H2O}"}</code>. A cell with a comma must be in quotes; Excel and Sheets do this automatically.
        </li>
        <li>
          <strong>Range answers in Excel:</strong> format the column as <strong>Text</strong> first, or Excel turns{" "}
          <code>2-5</code> into a date.
        </li>
        <li>
          <strong>Paragraphs:</strong> give rows the same <code>paragraph_group</code> (e.g. <code>P1</code>) and
          write the passage only on the first row.
        </li>
        {/* Images ZIP upload is disabled for now
        <li>
          <strong>Images:</strong> put the file name in the cell (e.g. <code>circuit.png</code>) and upload a ZIP
          with those files. JPG, PNG, WebP or GIF, max 250 KB each.
        </li> */}
        <li>
          <strong>To edit many questions:</strong> Export CSV → edit → Import with <strong>Update</strong>. Don't
          change the <code>question_id</code> column.
        </li>
      </ul>
      {isSeries && nested && (
        <>
          <p className="iq-help-heading">Subjects and their sections</p>
          <ul>
            <li>
              <strong>This test is split into subjects.</strong> Every row needs the subject in <code>subject</code>{" "}
              (e.g. <code>Physics</code>) and the section inside it in <code>section</code> (e.g.{" "}
              <code>Section A</code>). Questions always go into a section, never straight into a subject.
            </li>
            <li>
              Section names are looked up inside their subject, so <code>Section A</code> of Physics and{" "}
              <code>Section A</code> of Chemistry are two different sections.
            </li>
            <li>
              <code>subject_description</code> holds the subject's instructions. Write it <strong>once</strong>, on
              any one row of that subject.
            </li>
            <li>
              {timedSections ? (
                <>
                  Each new section needs its time in <code>section_duration_minutes</code>, once, on one of its rows.
                  Subjects have no timer of their own.
                </>
              ) : (
                <>
                  This test has one timer, so you can leave <code>section_duration_minutes</code> blank.
                </>
              )}{" "}
              <code>section_total_questions</code> and <code>section_description</code> work as usual, once per
              section.
            </li>
            <li>
              To only create a subject, fill just <code>subject</code> and <code>subject_description</code>. To only
              create a section, fill the subject, section and section columns and leave the question columns empty.
            </li>
            <li>
              Existing subjects and sections are never deleted. To change an existing description, duration or target,
              turn on <strong>Update section settings</strong>.
            </li>
          </ul>
        </>
      )}
      {isSeries && !nested && (
        <>
          <p className="iq-help-heading">Sections in the same file</p>
          <ul>
            <li>
              {timedSections ? (
                <>
                  <strong>Set up the whole test in one file.</strong> Put the section name in <code>section</code> on
                  every row. Give each new section its duration in <code>section_duration_minutes</code>{" "}
                  <strong>once</strong>, on any one of its rows, usually the first.
                </>
              ) : (
                <>
                  Put the section name in <code>section</code> on every row. This test has one timer, so you can leave{" "}
                  <code>section_duration_minutes</code> blank.
                </>
              )}
            </li>
            <li>
              <code>section_total_questions</code> is the target number of questions. Leave it blank to use the number
              of questions in the file.
            </li>
            <li>
              <code>section_description</code> holds the instructions students see. Plain text is fine; leave a blank
              line between paragraphs.
            </li>
            <li>To only create sections, fill just the section columns and leave the question columns empty.</li>
            <li>
              Section names are matched ignoring upper/lower case and extra spaces, so <code>physics</code> finds{" "}
              <code>Physics</code>. A different spelling creates a new section; the preview warns when a name looks
              like a typo.
            </li>
            <li>
              Existing sections are never deleted. To change an existing section's duration or target, turn on{" "}
              <strong>Update section settings</strong>.
            </li>
          </ul>
        </>
      )}
    </details>
  );
}

function RowDetails({ row, errors, warnings }) {
  const options = row.options || [];
  const columnOne = options.filter((o) => /^[A-F]$/.test(o.key));
  const columnTwo = options.filter((o) => /^[P-U]$/.test(o.key));
  const isMatch = row.question_type === "MATCH";

  const renderOption = (o) => (
    <div key={o.key} className="iq-option">
      <span className="iq-option-key" title={isMatch ? `CSV column option_${o.key.toLowerCase()}` : undefined}>
        {isMatch ? matchLabel(o.key) : o.key}
      </span>
      <div className="iq-option-body">
        <MathText text={o.option_en} empty={<span className="text-muted">—</span>} />
        {o.option_hi && (
          <div className="iq-hi">
            <MathText text={o.option_hi} />
          </div>
        )}
      </div>
      {(o.has_image_en || o.has_image_hi) && (
        <span className="badge badge-neutral" title="This option has an image">
          <i className="bi bi-image"></i> {[o.has_image_en && "EN", o.has_image_hi && "HI"].filter(Boolean).join(" + ")}
        </span>
      )}
      {isMatch && o.match_key && (
        <span className="badge badge-success">
          {matchLabel(o.match_key)} ↔ {matchLabel(o.key)}
        </span>
      )}
    </div>
  );

  return (
    <div className="iq-details">
      {row.paragraph_en && (
        <div className="iq-details-block">
          <span className="iq-label">Passage</span>
          <MathText text={row.paragraph_en} />
        </div>
      )}
      <div className="iq-details-block">
        <span className="iq-label">
          Question {row.has_question_image && <i className="bi bi-image" title="Has an image"></i>}
        </span>
        <MathText text={row.question_en} empty={<span className="text-muted">No English text</span>} />
        {row.question_hi && (
          <div className="iq-hi">
            <MathText text={row.question_hi} />
          </div>
        )}
      </div>

      {isMatch ? (
        <div className="iq-match">
          <div>
            <span className="iq-label">Column I</span>
            {columnOne.map(renderOption)}
          </div>
          <div>
            <span className="iq-label">Column II</span>
            {columnTwo.map(renderOption)}
          </div>
        </div>
      ) : (
        options.length > 0 && (
          <div className="iq-details-block">
            <span className="iq-label">Options</span>
            {options.map(renderOption)}
          </div>
        )
      )}

      <div className="iq-details-meta">
        {(row.topic_name || row.topic_name_hi) && (
          <span className="badge badge-neutral">
            <i className="bi bi-tag"></i> {[row.topic_name, row.topic_name_hi].filter(Boolean).join(" / ")}
          </span>
        )}
        {answerOf(row) && (
          <span className="badge badge-success">
            <i className="bi bi-check2"></i> {answerOf(row)}
          </span>
        )}
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger iq-inline-alert">
          <i className="bi bi-x-circle"></i>
          <div className="alert-body">
            <Messages groups={[[row.row, errors]]} />
          </div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="alert alert-warning iq-inline-alert">
          <i className="bi bi-exclamation-triangle"></i>
          <div className="alert-body">
            <Messages groups={[[row.row, warnings]]} />
          </div>
        </div>
      )}
    </div>
  );
}

// One line of text from a description: tags removed and entities (&nbsp;, &amp;) decoded
const stripHtml = (html) => richTextToPlain(String(html || "")).replace(/\s+/g, " ").trim();

const CHANGE_LABELS = {
  duration_minutes: (c) => `${c.from ?? "—"} → ${c.to} min`,
  total_questions: (c) => `Target ${c.from ?? "—"} → ${c.to}`,
};

// Series imports: which sections the file uses, creates or updates. Rows come from summary.by_section,
// joined with data.sections (created/updated sections only) by section_key.
function SectionsPanel({ bySection, sections, warnings, timing, onJumpToError }) {
  const details = new Map(sections.map((sec) => [sec.key, sec]));
  const anyDefaulted = sections.some((sec) => sec.total_questions_defaulted);

  return (
    <div className="table-card iq-block">
      <div className="iq-panel-title">
        <i className="bi bi-layers"></i> Sections
      </div>
      {timing?.sectional_timing === false && (
        <p className="iq-panel-note">
          <i className="bi bi-stopwatch"></i> One timer for the whole test
          {timing.test_duration_minutes ? `: ${timing.test_duration_minutes} min` : ""}. Section durations are not
          used.
        </p>
      )}
      <div className="table-scroll">
        <table className="data-table iq-table iq-sections">
          <thead>
            <tr>
              <th>Section</th>
              <th>Action</th>
              <th>Duration</th>
              <th>Questions (limit)</th>
              <th>In this file</th>
            </tr>
          </thead>
          <tbody>
            {bySection.map((s) => {
              const key = sectionKeyOf(s);
              const detail = details.get(key);
              const action = s.action || detail?.action || "existing";
              const changes = detail?.changes || {};
              const limit = detail?.total_questions ?? s.total_questions_limit;
              const over = limit > 0 && s.after_import > limit;
              const hasErrors = s.has_errors || detail?.has_errors;
              const typo =
                action === "create" &&
                warnings.find((w) => /did you mean/i.test(w.message) && w.message.includes(`'${s.section_name}'`));

              return (
                <tr key={key} className={hasErrors ? "iq-section-invalid" : undefined}>
                  <td>
                    <strong>{s.section_name}</strong>
                    {typo && (
                      <div className="iq-typo">
                        <i className="bi bi-exclamation-triangle"></i> {typo.message}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="iq-section-action">
                      {action === "create" && <span className="badge badge-success">New</span>}
                      {action === "update" && <span className="badge badge-info">Updated</span>}
                      {hasErrors && <span className="badge badge-danger">Errors</span>}
                    </div>
                    {Object.entries(changes)
                      .filter(([field]) => CHANGE_LABELS[field])
                      .map(([field, change]) => (
                        <small key={field} className="iq-change">
                          {CHANGE_LABELS[field](change)}
                        </small>
                      ))}
                    {changes.description && (
                      <small className="iq-change" title={stripHtml(changes.description.to)}>
                        Description changed
                      </small>
                    )}
                    {hasErrors && detail?.error_row && (
                      <button type="button" className="iq-link" onClick={() => onJumpToError(detail.error_row)}>
                        See row {detail.error_row}
                      </button>
                    )}
                  </td>
                  <td className="iq-nowrap">{detail?.duration_minutes ? `${detail.duration_minutes} min` : "—"}</td>
                  <td className={over ? "iq-over" : undefined}>
                    <span className={detail?.total_questions_defaulted ? "iq-defaulted" : undefined}>
                      {limit || "—"}
                    </span>
                    {detail?.total_questions_defaulted && <em className="text-muted"> · defaulted</em>}
                    {over && (
                      <small className="iq-change">
                        <i className="bi bi-exclamation-triangle"></i> {s.after_import} after import
                      </small>
                    )}
                  </td>
                  <td>
                    {s.rows}
                    {s.existing_questions > 0 && <small className="text-muted"> ({s.existing_questions} existing)</small>}
                    {s.to_delete > 0 && <span className="text-danger"> (−{s.to_delete})</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {anyDefaulted && (
        <p className="iq-panel-hint">
          <span className="iq-defaulted">Highlighted</span> limits were set from the number of questions in the file.
          Fill <code>section_total_questions</code> to choose them.
        </p>
      )}
    </div>
  );
}

export default function ImportQuestionsModal({
  sectionId,
  testSeriesId,
  scopeLabel,
  sectionNames,
  onClose,
  onImported,
  defaultCreateSections = false,
  sectionalTiming = true, // the test's timing mode, until a preview reports it
  nestedSections = false, // the test is split into subjects (subject + section columns)
}) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  // const [images, setImages] = useState(null);
  const [mode, setMode] = useState("append");
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [createSections, setCreateSections] = useState(defaultCreateSections); // series mode only
  const [updateSections, setUpdateSections] = useState(false); // series mode only
  const [busy, setBusy] = useState(null); // null | { kind: "preview" | "commit" | "undo", message }
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false); // 409 on commit: offer "Preview again"
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null); // data from /preview
  const [result, setResult] = useState(null); // data from /commit
  const [rowFilter, setRowFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all"); // a section_key, or "all"
  const [expanded, setExpanded] = useState(() => new Set());
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(true);
  const [rowLimit, setRowLimit] = useState(ROWS_PAGE);

  const isSeries = !sectionId;
  const scope = isSeries ? "series" : "section";
  // After a preview, trust the server's timing: it may have changed since the page loaded
  const timedSections = preview?.timing ? preview.timing.sectional_timing !== false : sectionalTiming !== false;
  const nested = isSeries && (preview ? Boolean(preview.nested_sections) : Boolean(nestedSections));

  const closeDialog = () => {
    if (!busy) onClose();
  };

  const pickFile = (next) => {
    setError(null);
    if (next && next.size > MAX_CSV_BYTES) {
      setError(`The CSV must be at most 5 MB; "${next.name}" is ${formatSize(next.size)}.`);
      return;
    }
    setFile(next);
  };

  // const pickImages = (next) => {
  //   setError(null);
  //   if (next && next.size > MAX_ZIP_BYTES) {
  //     setError(`The images ZIP must be at most 50 MB; "${next.name}" is ${formatSize(next.size)}.`);
  //     return;
  //   }
  //   setImages(next);
  // };

  const runPreview = async ({ skipInvalid: skipOverride, message } = {}) => {
    const skip = skipOverride ?? skipInvalid;
    if (!file) return;
    if (file.size > MAX_CSV_BYTES) return setError("The CSV must be at most 5 MB.");
    // if (images && images.size > MAX_ZIP_BYTES) return setError("The images ZIP must be at most 50 MB.");

    let rowCount = null;
    try {
      rowCount = countCsvRows(await file.text());
    } catch {
      // The server reports unreadable files; the count is only for the loading message
    }

    setBusy({
      kind: "preview",
      message: message || (rowCount ? `Checking ${plural(rowCount, "row")}…` : `Checking ${file.name}…`),
    });
    setError(null);
    setConflict(false);
    if (!message) setNotice(null);
    try {
      const { status, data } = await previewQuestionImport({
        file,
        // images,
        sectionId,
        testSeriesId,
        mode,
        skipInvalid: skip,
        createSections,
        updateSections,
      });
      if (status === 200 && data?.success && serverLacksSubjects(data.data)) {
        // An API that predates subjects reports "Unknown columns ignored: subject" and would import every
        // row into flat sections, so stop here instead of offering to commit
        setNotice(null);
        setError(OUTDATED_SERVER_MESSAGE);
        setStep("upload");
      } else if (status === 200 && data?.success) {
        const next = withoutImageWarnings(data.data);
        setSkipInvalid(skip);
        setPreview(next);
        setRowFilter("all");
        setSectionFilter("all");
        setExpanded(new Set());
        setShowAllErrors(false);
        // Typo warnings ("Did you mean 'Physics'?") can add an unwanted section, so show them even with errors
        setWarningsOpen(
          !(next.errors || []).length || (next.warnings || []).some((w) => /did you mean/i.test(w.message))
        );
        setRowLimit(ROWS_PAGE);
        setStep("preview");
      } else if (data) {
        setNotice(null);
        setError(data.message || "The file could not be checked.");
        setStep("upload");
      }
    } catch {
      setNotice(null);
      setError("Could not reach the server. Check your connection and try again.");
      setStep("upload");
    } finally {
      setBusy(null);
    }
  };

  const commit = async () => {
    if (busy || !preview?.can_commit) return;
    const { to_create: toCreate, to_delete: toDelete } = preview.summary;
    if (toDelete > 0) {
      const res = await showConfirmation(
        `Delete ${plural(toDelete, "existing question")} and import ${toCreate}? This cannot be undone.`,
        "Replace questions?",
        "Delete and import",
        "Cancel",
        "danger"
      );
      if (!res.isConfirmed) return;
    }

    setBusy({ kind: "commit" });
    setError(null);
    setConflict(false);
    setNotice(null);
    let expired = false;
    try {
      const { status, data } = await commitQuestionImport(preview.import_id);
      if (status === 200 && data?.success) {
        setResult({ ...withoutImageWarnings(data.data), message: data.message });
        setStep("done");
        onImported?.();
      } else if (status === 404) {
        expired = true;
      } else if (status === 409) {
        setError(data?.message || "Something changed since the preview.");
        setConflict(true);
      } else if (data) {
        setError(data.message || "Import failed. Nothing was saved.");
      }
    } catch {
      setError("Could not reach the server. Nothing was saved; check your connection and try again.");
    } finally {
      setBusy(null);
    }

    if (expired) {
      setNotice("The preview expired, so the file was checked again. Review it and confirm.");
      await runPreview({ message: "This preview expired. Checking the file again…" });
    }
  };

  const undo = async () => {
    // Subjects are sections too for undo: they are removed with the sections this import created
    const res = await confirmUndoImport(result.created, result.sections_created + (result.subjects_created || 0));
    if (!res.isConfirmed) return;
    setBusy({ kind: "undo" });
    try {
      const { status, data } = await undoImportBatch(result.batch_id);
      if (status === 200 && data?.success) {
        showSuccess(data.message || "The import was undone.", "Import undone");
        onImported?.();
        onClose();
      } else if (data) {
        showError(data.message || "Could not undo the import.");
      }
    } catch {
      showError("Could not reach the server. Check your connection and try again.");
    } finally {
      setBusy(null);
    }
  };

  // ---- derived preview data ----
  const errorGroups = useMemo(() => groupByRow(preview?.errors), [preview]);
  const warningGroups = useMemo(() => groupByRow(preview?.warnings), [preview]);
  const errorsByRow = useMemo(() => new Map(errorGroups), [errorGroups]);
  const warningsByRow = useMemo(() => new Map(warningGroups), [warningGroups]);

  const rows = useMemo(() => preview?.rows || [], [preview]);
  const hasErrors = (r) => r.status === "invalid" || r.error_count > 0;
  const rowMatchers = {
    all: () => true,
    errors: hasErrors,
    warnings: (r) => r.warning_count > 0,
    create: (r) => r.action === "create",
    update: (r) => r.action === "update",
  };
  const inSection = (r) => sectionFilter === "all" || sectionKeyOf(r) === sectionFilter;
  const filteredRows = rows.filter((r) => inSection(r) && rowMatchers[rowFilter](r));

  // Jump from the Sections panel to the row that holds a section's error
  const jumpToError = (row) => {
    setShowAllErrors(true);
    setTimeout(() => {
      document.getElementById(`iq-error-row-${row}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const toggleRow = (rowNumber) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });

  // ---- steps ----
  const renderUpload = () => {
    const selectedMode = MODES.find((m) => m.value === mode);
    return (
      <>
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-circle"></i>
            <div className="alert-body">{error}</div>
          </div>
        )}

        <div className="iq-sample">
          <div className="iq-sample-icon">
            <i className="bi bi-file-earmark-spreadsheet"></i>
          </div>
          <div className="iq-sample-body">
            <strong>
              {nested ? "This test is split into subjects — use the Subjects + sections sample." : "New to CSV import? Start from the sample."}
            </strong>
            <span>
              {nested ? (
                <>
                  It creates Physics, Chemistry and Mathematics, each with a Section A and a Section B, with their
                  descriptions, settings and filled-in questions. Every row names its <code>subject</code> and its{" "}
                  <code>section</code>. The plain sections sample will not work for this test.
                </>
              ) : isSeries ? (
                <>
                  The sections sample builds a whole test: it creates Physics, Chemistry and Mathematics with their
                  settings and one filled-in question of every type.
                  {sectionNames?.length > 0 && " The other sample spreads the questions across this series' sections."}
                </>
              ) : (
                "It has one filled-in question of every type."
              )}{" "}
              Replace the rows with your own and upload the file here.
            </span>
          </div>
          <div className="iq-sample-actions">
            {nested && (
              <button
                type="button"
                className="btn-soft btn-sm"
                onClick={() => downloadSubjectsSampleCsv(timedSections)}
                title="subjects_sections_questions_sample.csv"
              >
                <i className="bi bi-download"></i> Subjects → sections sample
              </button>
            )}
            {isSeries && !nested && (
              <button
                type="button"
                className="btn-soft btn-sm"
                onClick={() => downloadSectionsSampleCsv(timedSections)}
                title="sections_questions_sample.csv"
              >
                <i className="bi bi-download"></i> Sections + questions sample
              </button>
            )}
            {(!isSeries || (!nested && sectionNames?.length > 0)) && (
              <button
                type="button"
                className={isSeries ? "btn-secondary btn-sm" : "btn-soft btn-sm"}
                onClick={() => downloadSampleCsv(isSeries ? sectionNames : undefined)}
              >
                <i className="bi bi-download"></i> {isSeries ? "Sample for existing sections" : "Download sample CSV"}
              </button>
            )}
          </div>
        </div>

        {/* Which sample goes with which paper structure, so the other one can still be found */}
        {isSeries && (
          <div className="iq-templates">
            <span>
              {nested ? (
                <>
                  Use <code>subjects_sections_questions_sample.csv</code> (columns <code>subject</code>,{" "}
                  <code>subject_description</code>, <code>section</code>, …).
                </>
              ) : (
                <>
                  Paper split into subjects (Physics → Section A, Section B)? That needs a test created with{" "}
                  <strong>Subjects with sections</strong> and the
                </>
              )}
            </span>
            {!nested && (
              <button
                type="button"
                className="iq-link"
                onClick={() => downloadSubjectsSampleCsv(timedSections)}
                title="subjects_sections_questions_sample.csv"
              >
                <strong>Subjects → sections sample</strong>
              </button>
            )}
          </div>
        )}

        {isSeries && (
          <div className="iq-templates">
            <span>Blank template:</span>
            <button
              type="button"
              className="iq-link"
              onClick={() => downloadImportTemplate("ALL", "series", true, nested).catch((e) => showError(e.message))}
            >
              <strong>{nested ? "Subjects + sections + questions" : "Sections + questions"}</strong>
            </button>
          </div>
        )}

        {/* The questions-only templates have no subject column, so they do not fit a subject-wise test */}
        {!nested && (
        <div className="iq-templates">
          <span>{isSeries ? "Questions only:" : "Blank template:"}</span>
          {TEMPLATE_LINKS.map(([type, label], i) => (
            <React.Fragment key={type}>
              {i > 0 && <span className="iq-dot">·</span>}
              <button
                type="button"
                className="iq-link"
                onClick={() => downloadImportTemplate(type, scope).catch((e) => showError(e.message))}
              >
                {label}
              </button>
            </React.Fragment>
          ))}
        </div>
        )}

        {isSeries && (
          <div className="alert alert-info">
            <i className="bi bi-info-circle"></i>
            <div className="alert-body">
              {nested ? (
                <>
                  Your CSV needs a <code>subject</code> column (e.g. Physics) and a <code>section</code> column (e.g.
                  Section A) on every row. <code>subject_description</code> is optional, once per subject.
                </>
              ) : (
                <>
                  Your CSV needs a <code>section</code> column with the section name (e.g. Physics) or id on every row.
                </>
              )}
              {createSections && timedSections && (
                <>
                  {" "}
                  New sections also need <code>section_duration_minutes</code> on one of their rows.
                </>
              )}
            </div>
          </div>
        )}

        <FilePicker
          id="iqCsvFile"
          label={
            <>
              CSV file <span className="required">*</span>
            </>
          }
          accept=".csv,text/csv"
          icon="bi bi-filetype-csv"
          file={file}
          onChange={pickFile}
          hint="Max 5 MB and 500 questions."
        />

        {/* Images ZIP upload is disabled for now
        <FilePicker
          id="iqZipFile"
          label={
            <>
              Images ZIP <span className="optional">Optional</span>
            </>
          }
          accept=".zip,application/zip"
          icon="bi bi-file-earmark-zip"
          file={images}
          onChange={pickImages}
          hint={
            <>
              Only needed if the CSV names image files, e.g. <code>circuit.png</code>. Max 50 MB.
            </>
          }
        /> */}

        <div className="form-group">
          <label>Import mode</label>
          <div className="segmented iq-modes" role="radiogroup" aria-label="Import mode">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={mode === m.value}
                className={`${mode === m.value ? "active" : ""} ${m.danger ? "iq-danger" : ""}`}
                onClick={() => setMode(m.value)}
              >
                {m.danger && <i className="bi bi-exclamation-triangle"></i>} {m.label}
              </button>
            ))}
          </div>
          <span className={`form-hint ${selectedMode.danger ? "iq-danger-text" : ""}`}>{selectedMode.help}</span>
        </div>

        {isSeries && (
          <>
            <label className="iq-check">
              <input type="checkbox" checked={createSections} onChange={(e) => setCreateSections(e.target.checked)} />
              <span>
                <strong>{nested ? "Create missing subjects and sections" : "Create missing sections"}</strong>
                <small>
                  {nested
                    ? "Subjects, and sections inside a subject, that don't exist yet are created."
                    : "Section names in the file that don't exist yet are created."}{" "}
                  {timedSections ? (
                    <>
                      Each new section needs <code>section_duration_minutes</code> on one of its rows.
                    </>
                  ) : (
                    <>
                      <code>section_duration_minutes</code> is optional: this test has one timer for the whole test.
                    </>
                  )}
                </small>
              </span>
            </label>
            <label className="iq-check">
              <input type="checkbox" checked={updateSections} onChange={(e) => setUpdateSections(e.target.checked)} />
              <span>
                <strong>Update section settings</strong>
                <small>
                  Filled {nested ? "subject and section" : "section"} columns change the settings of existing{" "}
                  {nested ? "subjects and sections" : "sections"}. Blank cells never clear a setting.
                  Undo does not change settings back.
                </small>
              </span>
            </label>
          </>
        )}

        <label className="iq-check">
          <input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} />
          <span>
            <strong>Skip invalid rows</strong>
            <small>Import the valid rows and skip rows with errors.</small>
          </span>
        </label>

        <CsvHelp isSeries={isSeries} timedSections={timedSections} nested={nested} />
      </>
    );
  };

  const renderPreview = () => {
    const { summary } = preview;
    const bySection = summary.by_section || [];
    const deletions = bySection.filter((s) => s.to_delete > 0);
    const errorLimit = showAllErrors ? 0 : MAX_ERROR_GROUPS;
    const errorRowGroups = errorGroups.filter(([row]) => row !== "file").length;

    return (
      <>
        {notice && (
          <div className="alert alert-info">
            <i className="bi bi-info-circle"></i>
            <div className="alert-body">{notice}</div>
          </div>
        )}
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-circle"></i>
            <div className="alert-body">{error}</div>
            {conflict && (
              <div className="alert-actions">
                <button type="button" className="btn-secondary btn-sm" onClick={() => runPreview()}>
                  <i className="bi bi-arrow-clockwise"></i> Preview again
                </button>
              </div>
            )}
          </div>
        )}

        <div className="iq-summary">
          <p>
            <strong>{preview.message}</strong>
            {preview.expires_at && (
              <span className="text-muted"> Confirm before {formatTime(preview.expires_at)}.</span>
            )}
          </p>
          <div className="iq-summary-badges">
            {summary.subjects_to_create > 0 && (
              <span className="badge badge-success">
                <i className="bi bi-collection"></i> {plural(summary.subjects_to_create, "new subject")}
              </span>
            )}
            {summary.subjects_to_update > 0 && (
              <span className="badge badge-info">
                <i className="bi bi-collection"></i> {plural(summary.subjects_to_update, "subject")} updated
              </span>
            )}
            {summary.sections_to_create > 0 && (
              <span className="badge badge-success">
                <i className="bi bi-layers"></i> {plural(summary.sections_to_create, "new section")}
              </span>
            )}
            {summary.sections_to_update > 0 && (
              <span className="badge badge-info">
                <i className="bi bi-layers"></i> {plural(summary.sections_to_update, "section")} updated
              </span>
            )}
            <span className="badge badge-success">{summary.to_create} new</span>
            {summary.to_update > 0 && <span className="badge badge-info">{summary.to_update} updated</span>}
            {summary.to_delete > 0 && <span className="badge badge-danger">{summary.to_delete} deleted</span>}
            {summary.invalid_rows > 0 && (
              <span className="badge badge-warning">
                {summary.invalid_rows} with errors{preview.skip_invalid ? " (skipped)" : ""}
              </span>
            )}
            {Object.entries(summary.by_type || {}).map(([type, n]) => (
              <span key={type} className="badge badge-neutral">
                {SHORT_TYPE_LABELS[type] || type} {n}
              </span>
            ))}
          </div>
        </div>

        {summary.to_delete > 0 && (
          <div className="alert alert-danger">
            <i className="bi bi-trash3"></i>
            <div className="alert-body">
              <strong>{plural(summary.to_delete, "existing question")} will be permanently deleted</strong>
              {deletions.length > 0 && (
                <> from: {deletions.map((s) => `${s.section_name} (${s.to_delete})`).join(", ")}</>
              )}
              .
            </div>
          </div>
        )}

        {nested && (preview.subjects || []).length > 0 && (
          <div className="table-card iq-block">
            <div className="iq-panel-title">
              <i className="bi bi-collection"></i> Subjects
            </div>
            <div className="table-scroll">
              <table className="data-table iq-table iq-sections">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Action</th>
                    <th>Sections in this file</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.subjects.map((subject) => (
                    <tr key={subject.key}>
                      <td>
                        <strong>{subject.name}</strong>
                      </td>
                      <td>
                        {subject.action === "create" && <span className="badge badge-success">New</span>}
                        {subject.action === "update" && <span className="badge badge-info">Description updated</span>}
                        {subject.action === "existing" && <span className="text-muted">Existing</span>}
                      </td>
                      <td>{subject.sections}</td>
                      <td className="text-muted" title={stripHtml(subject.description || "")}>
                        {truncateMath(stripHtml(subject.description || ""), 80) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isSeries && bySection.length > 0 && (
          <SectionsPanel
            bySection={bySection}
            sections={preview.sections || []}
            warnings={preview.warnings || []}
            timing={preview.timing}
            onJumpToError={jumpToError}
          />
        )}

        {!isSeries && bySection.length > 1 && (
          <div className="table-card iq-block">
            <div className="table-scroll">
              <table className="data-table iq-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Rows</th>
                    <th>Existing</th>
                    <th>After import / Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {bySection.map((s) => {
                    const over = s.total_questions_limit > 0 && s.after_import > s.total_questions_limit;
                    return (
                      <tr key={sectionKeyOf(s)}>
                        <td>
                          <strong>{s.section_name}</strong>
                        </td>
                        <td>{s.rows}</td>
                        <td>
                          {s.existing_questions}
                          {s.to_delete > 0 && <span className="text-danger"> (−{s.to_delete})</span>}
                        </td>
                        <td className={over ? "iq-over" : ""}>
                          {over && <i className="bi bi-exclamation-triangle"></i>} {s.after_import} /{" "}
                          {s.total_questions_limit || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {errorGroups.length > 0 && (
          <div className="alert alert-danger iq-panel">
            <i className="bi bi-x-circle"></i>
            <div className="alert-body">
              <span className="alert-title">
                {plural(preview.errors.length, "error")}
                {errorRowGroups > 0 && ` in ${plural(errorRowGroups, "row")}`}
              </span>
              <Messages groups={errorGroups} limit={errorLimit} idPrefix="iq-error-row-" />
              {errorGroups.length > MAX_ERROR_GROUPS && (
                <button type="button" className="iq-link" onClick={() => setShowAllErrors((v) => !v)}>
                  {showAllErrors ? "Show fewer" : `Show all ${errorGroups.length}`}
                </button>
              )}
            </div>
          </div>
        )}

        {warningGroups.length > 0 && (
          <details
            className="alert alert-warning iq-panel iq-collapsible"
            open={warningsOpen}
            onToggle={(e) => setWarningsOpen(e.currentTarget.open)}
          >
            <summary>
              <i className="bi bi-exclamation-triangle"></i>
              <span className="alert-title">
                {plural(preview.warnings.length, "warning")} <small>(these don't block the import)</small>
              </span>
              <i className="bi bi-chevron-down iq-caret"></i>
            </summary>
            <Messages groups={warningGroups} />
          </details>
        )}

        {rows.length === 0 ? (
          <div className="alert alert-info">
            <i className="bi bi-layers"></i>
            <div className="alert-body">No questions in this file — only sections will be set up.</div>
          </div>
        ) : (
          <>
            <div className="iq-filters iq-block">
              <div className="chip-group">
                {ROW_FILTERS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`chip ${rowFilter === key ? "active" : ""}`}
                    onClick={() => {
                      setRowFilter(key);
                      setRowLimit(ROWS_PAGE);
                    }}
                  >
                    {label}{" "}
                <span className="chip-count">{rows.filter((r) => inSection(r) && rowMatchers[key](r)).length}</span>
                  </button>
                ))}
              </div>
              {isSeries && bySection.length > 1 && (
                <select
                  className="form-control iq-section-filter"
                  value={sectionFilter}
                  onChange={(e) => {
                    setSectionFilter(e.target.value);
                    setRowLimit(ROWS_PAGE);
                  }}
                  aria-label="Filter rows by section"
                >
                  <option value="all">All sections</option>
                  {bySection.map((sec) => (
                    <option key={sectionKeyOf(sec)} value={sectionKeyOf(sec)}>
                      {sec.section_name}
                      {sec.section_id == null ? " (new)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="table-card">
              <div className="table-scroll">
                <table className="data-table iq-table iq-rows">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Status</th>
                      <th>Action</th>
                      {isSeries && <th>Section</th>}
                      <th>Type</th>
                      <th>Question</th>
                      <th>Answer</th>
                      <th>Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={isSeries ? 8 : 7} className="iq-empty">
                          No rows match this filter.
                        </td>
                      </tr>
                    )}
                    {filteredRows.slice(0, rowLimit).map((r) => {
                      const open = expanded.has(r.row);
                      const invalid = hasErrors(r);
                      return (
                        <React.Fragment key={r.row}>
                          <tr
                            className={`iq-row ${invalid ? "invalid" : ""} ${open ? "open" : ""}`}
                            onClick={() => toggleRow(r.row)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleRow(r.row);
                              }
                            }}
                            tabIndex={0}
                            aria-expanded={open}
                          >
                            <td className="mono">
                              <i className={`bi bi-chevron-${open ? "down" : "right"} iq-row-caret`}></i> {r.row}
                            </td>
                            <td className="iq-nowrap">
                              {invalid ? (
                                <span className="badge badge-danger" title="Has errors">
                                  ✕
                                </span>
                              ) : (
                                <span className="badge badge-success" title="Valid">
                                  ✓
                                </span>
                              )}{" "}
                              {r.warning_count > 0 && (
                                <span className="badge badge-warning" title={plural(r.warning_count, "warning")}>
                                  ⚠
                                </span>
                              )}
                            </td>
                            <td className="iq-nowrap">{r.action === "update" ? `Update #${r.question_id}` : "New"}</td>
                            {isSeries && (
                              <td>
                                {r.section_name || "—"}
                                {r.section_id == null && r.section_name && (
                                  <span className="badge badge-success iq-new-badge" title="This import creates the section">
                                    New
                                  </span>
                                )}
                              </td>
                            )}
                            <td className="iq-nowrap">
                              {SHORT_TYPE_LABELS[r.question_type] || QUESTION_TYPE_LABELS[r.question_type] || r.question_type || "—"}
                            </td>
                            <td className="iq-question">
                              <MathText
                                text={truncateMath(richTextToPlain(r.question_en))}
                                empty={<span className="text-muted">No question text</span>}
                              />
                              {r.has_question_image && <i className="bi bi-image iq-img-flag" title="Has an image"></i>}
                            </td>
                            <td className="mono iq-nowrap">{answerOf(r) || "—"}</td>
                            <td className="iq-nowrap">
                              {r.marks != null ? `+${r.marks}` : "—"} / {r.negative_marks != null ? `−${r.negative_marks}` : "—"}
                            </td>
                          </tr>
                          {open && (
                            <tr className="iq-details-row">
                              <td colSpan={isSeries ? 8 : 7}>
                                <RowDetails
                                  row={r}
                                  errors={errorsByRow.get(r.row) || []}
                                  warnings={warningsByRow.get(r.row) || []}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredRows.length > rowLimit && (
                <div className="iq-more">
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setRowLimit(Infinity)}>
                    Show all {filteredRows.length} rows
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  const renderDone = () => (
    <div className="iq-done">
      <div className="iq-done-icon">
        <i className="bi bi-check-lg"></i>
      </div>
      <h4>{result.message || `Imported ${plural(result.created, "question")}.`}</h4>
      <p className="text-muted">
        {[
          result.created > 0 && `${result.created} added`,
          result.updated > 0 && `${result.updated} updated`,
          result.deleted > 0 && `${result.deleted} deleted`,
          result.skipped > 0 && `${plural(result.skipped, "row was", "rows were")} skipped`,
          result.subjects_created > 0 && `${plural(result.subjects_created, "subject")} created`,
          result.subjects_updated > 0 && `${plural(result.subjects_updated, "subject")} updated`,
          result.sections_created > 0 && `${plural(result.sections_created, "section")} created`,
          result.sections_updated > 0 && `${plural(result.sections_updated, "section")} updated`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {isSeries && (result.by_section || []).length > 0 && (
        <div className="iq-summary-badges iq-done-sections">
          {result.by_section.map((s) => {
            const name =
              s.section_name ||
              (preview?.summary?.by_section || []).find((p) => p.section_id === s.section_id)?.section_name ||
              `Section ${s.section_id}`;
            const tone = { created: "badge-success", updated: "badge-info" }[s.section_action] || "badge-neutral";
            return (
              <span key={s.section_id} className={`badge ${tone}`}>
                {s.section_action === "created" && "New · "}
                {s.section_action === "updated" && "Updated · "}
                {name}: +{s.created}
                {s.updated > 0 && `, ${s.updated} updated`}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  // ---- footer ----
  const previewing = busy?.kind === "preview";
  let footer = null;
  if (previewing) {
    footer = null;
  } else if (step === "upload") {
    footer = (
      <>
        <button type="button" className="btn-secondary" onClick={closeDialog}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={() => runPreview()} disabled={!file || !!busy}>
          <i className="bi bi-eye"></i> Preview
        </button>
      </>
    );
  } else if (step === "preview") {
    const committing = busy?.kind === "commit";
    footer = (
      <>
        {!preview.can_commit && (
          <span className="iq-footer-hint">
            Fix the errors and upload again, or turn on <em>Skip invalid rows</em>.{" "}
            {!preview.skip_invalid && preview.summary.valid_rows > 0 && (
              <button type="button" className="iq-link" onClick={() => runPreview({ skipInvalid: true })}>
                Turn on skip &amp; re-check
              </button>
            )}
          </span>
        )}
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setError(null);
            setConflict(false);
            setNotice(null);
            setStep("upload");
          }}
          disabled={committing}
        >
          <i className="bi bi-arrow-left"></i> Back
        </button>
        <button
          type="button"
          className={preview.summary.to_delete > 0 ? "btn-danger-solid" : "btn-primary"}
          onClick={commit}
          disabled={!preview.can_commit || !!busy}
        >
          {committing ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
          {confirmLabel(preview.summary)}
        </button>
      </>
    );
  } else if (step === "done") {
    footer = (
      <>
        {(result.created > 0 || result.sections_created > 0 || result.subjects_created > 0) && (
          <button type="button" className="btn-danger iq-footer-left" onClick={undo} disabled={!!busy}>
            {busy?.kind === "undo" ? <span className="btn-spinner"></span> : <i className="bi bi-arrow-counterclockwise"></i>}
            Undo this import
          </button>
        )}
        <button type="button" className="btn-primary" onClick={onClose} disabled={!!busy}>
          {isSeries ? "Done" : "View questions"}
        </button>
      </>
    );
  }

  const subtitle = {
    upload: scopeLabel ? `Into ${scopeLabel}` : "Upload a CSV to check it",
    preview: `${file?.name || "CSV"} · ${MODE_LABELS[preview?.mode || mode]} mode${preview?.skip_invalid ? " · skipping invalid rows" : ""}`,
    done: scopeLabel ? `Into ${scopeLabel}` : undefined,
  }[step];

  return (
    <Modal
      size="xl"
      icon="bi bi-upload"
      title={
        step === "done"
          ? "Import complete"
          : isSeries
          ? createSections
            ? "Import sections and questions"
            : "Import questions into this series"
          : "Import questions"
      }
      subtitle={subtitle}
      onClose={closeDialog}
      closeOnOverlay={false}
      footer={footer}
    >
      <div className="iq">
        {previewing ? (
          <LoadingState message={busy.message} />
        ) : step === "upload" ? (
          renderUpload()
        ) : step === "preview" ? (
          renderPreview()
        ) : (
          renderDone()
        )}
      </div>
    </Modal>
  );
}
